import { ISaleOrder, ISaleOrderRefund } from '../models/sale-order.interface';
import { SaleOrderStatus } from 'src/app/shared/enums/sale-order-status.enum';

/** Debit = 1; credit refund SOs show raw amounts. */
const SALE_ORDER_TYPE_DEBIT = 1;
/** Matches backend RefundType.FULL_REFUND. */
const REFUND_TYPE_FULL = 0;

export type OrderRefundTotalsSource = {
    totalRefundAmount?: number | string | null;
    totalRefundVat?: number | string | null;
    grandTotal?: number | string | null;
    eciTotal?: number | string | null;
    eciVat?: number | string | null;
    total?: number | string | null;
    totalWithoutVat?: number | string | null;
    eciTotalEuro?: number | string | null;
    eciVatEuro?: number | string | null;
    theoreticalVat?: number | string | null;
    orderType?: number | null;
    status?: number | null;
    refundType?: number | null;
    latestFinancialStatusId?: number | null;
    refunds?: ISaleOrderRefund[];
};

/** Sum refund_amount / credit eci_vat from list API fields or details refunds[]. */
export function resolveRefundTotalsFromOrder(
    order: OrderRefundTotalsSource,
): { totalRefundAmount: number; totalRefundVat: number } {
    const fromApiAmount = Math.abs(Number(order?.totalRefundAmount ?? 0)) || 0;
    const fromApiVat = Math.abs(Number(order?.totalRefundVat ?? 0)) || 0;

    if (fromApiAmount > 0 || fromApiVat > 0) {
        return { totalRefundAmount: fromApiAmount, totalRefundVat: fromApiVat };
    }

    let totalRefundAmount = 0;
    let totalRefundVat = 0;
    for (const link of order?.refunds || []) {
        totalRefundAmount += Math.abs(Number(link?.refundAmount) || 0);
        totalRefundVat += Math.abs(Number(link?.refundOrder?.eciVat) || 0);
    }
    return { totalRefundAmount, totalRefundVat };
}

export function shouldApplyNetAmountsAfterRefunds(order: { orderType?: number | null }): boolean {
    const type = order?.orderType;
    return type == null || type === SALE_ORDER_TYPE_DEBIT;
}

/** Debit order with a full refund (financial history, legacy status, or refundType). */
export function isFullFinancialRefundOrder(order: OrderRefundTotalsSource): boolean {
    if (!shouldApplyNetAmountsAfterRefunds(order)) {
        return false;
    }
    if (Number(order?.latestFinancialStatusId) === SaleOrderStatus.FULL_REFUND) {
        return true;
    }
    if (Number(order?.status) === SaleOrderStatus.FULL_REFUND) {
        return true;
    }
    // Number(null) === 0, and FULL_REFUND is 0 — only compare when refundType is set.
    if (order?.refundType != null && Number(order.refundType) === REFUND_TYPE_FULL) {
        return true;
    }
    return false;
}

/** B2C/B2B list: full financial refund shows gross debit; partial refund deducts credit amounts. */
export function shouldDeductRefundFromListDisplay(order: OrderRefundTotalsSource): boolean {
    if (!shouldApplyNetAmountsAfterRefunds(order)) {
        return false;
    }
    if (isFullFinancialRefundOrder(order)) {
        return false;
    }
    return getTotalRefundAmount(order) > 0;
}

/** Original debit total before net refund deduction (uses refund sum when eciTotal was zeroed). */
export function getGrossDebitOrderTotal(order: OrderRefundTotalsSource): number {
    if (!shouldApplyNetAmountsAfterRefunds(order)) {
        return Number(order?.grandTotal ?? order?.eciTotal ?? order?.total ?? 0);
    }
    const absEci = Math.abs(Number(order?.eciTotal) || 0);
    const refund = getTotalRefundAmount(order);
    if (absEci > 0) {
        return absEci;
    }
    if (refund > 0) {
        return refund;
    }
    return Math.abs(Number(order?.grandTotal ?? order?.total ?? 0));
}

function getGrossDebitOrderVat(order: OrderRefundTotalsSource): number {
    if (!shouldApplyNetAmountsAfterRefunds(order)) {
        return Number(order?.eciVat) || 0;
    }
    const absVat = Math.abs(Number(order?.eciVat) || 0);
    const refundVat = getTotalRefundVatAmount(order);
    if (absVat > 0) {
        return absVat;
    }
    if (refundVat > 0) {
        return refundVat;
    }
    return 0;
}

function getGrossDebitAmountExcVat(order: OrderRefundTotalsSource): number {
    const fromTotalWithoutVat = Number(order?.totalWithoutVat);
    if (Number.isFinite(fromTotalWithoutVat) && fromTotalWithoutVat !== 0) {
        return Math.abs(fromTotalWithoutVat);
    }
    return Math.max(0, getGrossDebitOrderTotal(order) - getGrossDebitOrderVat(order));
}

function getDisplayRefundAmount(element: OrderRefundTotalsSource): number {
    return shouldDeductRefundFromListDisplay(element) ? getTotalRefundAmount(element) : 0;
}

function getDisplayRefundVatAmount(element: OrderRefundTotalsSource): number {
    if (!shouldDeductRefundFromListDisplay(element)) {
        return 0;
    }
    return getTotalRefundVatAmount(element);
}

/** Order view with resolved refund totals for net amount helpers. */
export function orderForNetAmountDisplay<T extends OrderRefundTotalsSource>(order: T): T & {
    totalRefundAmount: number;
    totalRefundVat: number;
} {
    const totals = resolveRefundTotalsFromOrder(order);
    return {
        ...order,
        totalRefundAmount: totals.totalRefundAmount,
        totalRefundVat: totals.totalRefundVat,
    };
}

export function getTotalRefundAmount(element: OrderRefundTotalsSource): number {
    return resolveRefundTotalsFromOrder(element).totalRefundAmount;
}

export function getTotalRefundVatAmount(element: OrderRefundTotalsSource): number {
    const totals = resolveRefundTotalsFromOrder(element);
    if (totals.totalRefundVat > 0) {
        return totals.totalRefundVat;
    }
    const orderTotal = Math.abs(Number(element?.eciTotal) || 0);
    const orderVat = Math.abs(Number(element?.eciVat) || 0);
    if (orderTotal > 0 && totals.totalRefundAmount > 0 && orderVat > 0) {
        return parseFloat((orderVat * (totals.totalRefundAmount / orderTotal)).toFixed(2));
    }
    return 0;
}

function netRatio(element: OrderRefundTotalsSource): number {
    const orderTotal = Math.abs(Number(element?.eciTotal) || 0);
    if (orderTotal <= 0) {
        return 1;
    }
    const net = Math.max(0, orderTotal - getDisplayRefundAmount(element));
    return net / orderTotal;
}

export function formatNetOrderTotal(element: OrderRefundTotalsSource): string {
    const net = Math.max(0, (Number(element?.eciTotal) || 0) - getDisplayRefundAmount(element));
    return net.toFixed(2);
}

export function formatNetVat(element: OrderRefundTotalsSource): string {
    if (!shouldDeductRefundFromListDisplay(element)) {
        return getGrossDebitOrderVat(element).toFixed(2);
    }
    const net = Math.max(0, (Number(element?.eciVat) || 0) - getDisplayRefundVatAmount(element));
    return net.toFixed(2);
}

export function formatNetAmountExcVat(element: OrderRefundTotalsSource): string {
    if (!shouldDeductRefundFromListDisplay(element)) {
        return getGrossDebitAmountExcVat(element).toFixed(2);
    }
    const refundExc = getDisplayRefundAmount(element) - getDisplayRefundVatAmount(element);
    const fromTotalWithoutVat = Number(element?.totalWithoutVat);
    if (Number.isFinite(fromTotalWithoutVat) && fromTotalWithoutVat !== 0) {
        return Math.max(0, fromTotalWithoutVat - refundExc).toFixed(2);
    }
    const netTotal = parseFloat(formatNetOrderTotal(element));
    const netVat = parseFloat(formatNetVat(element));
    return Math.max(0, netTotal - netVat).toFixed(2);
}

export function formatNetEciTotalEuro(element: OrderRefundTotalsSource): string {
    const euro = Number(element?.eciTotalEuro) || 0;
    return (euro * netRatio(element)).toFixed(2);
}

export function formatNetEciVatEuro(element: OrderRefundTotalsSource): string {
    const euro = Number(element?.eciVatEuro) || 0;
    return (euro * netRatio(element)).toFixed(2);
}

export function formatNetTheoreticalVat(element: OrderRefundTotalsSource): string {
    const vat = Number(element?.theoreticalVat) || 0;
    return (vat * netRatio(element)).toFixed(2);
}

export function formatGrossOrderTotal(element: OrderRefundTotalsSource): string {
    return getGrossDebitOrderTotal(element).toFixed(2);
}

export function getGrossAmountDisplayValues(info: OrderRefundTotalsSource): {
    sellOrderTotal: string;
    eciVat: string;
    sellOrderAmount: string;
    eciVatEuro: string;
    eciTotalEuro: string;
    theoreticalVat: string;
} {
    const totalWithoutVat = Number(info?.totalWithoutVat);
    const sellOrderAmount = Number.isFinite(totalWithoutVat) && totalWithoutVat !== 0
        ? Math.abs(totalWithoutVat).toFixed(2)
        : getGrossDebitAmountExcVat(info).toFixed(2);

    return {
        sellOrderTotal: formatGrossOrderTotal(info),
        eciVat: getGrossDebitOrderVat(info).toFixed(2),
        sellOrderAmount,
        eciVatEuro: info?.eciVatEuro != null ? Number(info.eciVatEuro).toFixed(2) : '0.00',
        eciTotalEuro: info?.eciTotalEuro != null ? Number(info.eciTotalEuro).toFixed(2) : '0.00',
        theoreticalVat: (Number(info?.theoreticalVat) || 0).toFixed(2),
    };
}

export function formatDisplayOrderTotal(element: ISaleOrder): string {
    if (!shouldDeductRefundFromListDisplay(element)) {
        return getGrossDebitOrderTotal(element).toFixed(2);
    }
    return formatNetOrderTotal(orderForNetAmountDisplay(element));
}
